import { doc, getDoc, serverTimestamp, setDoc, type Firestore } from 'firebase/firestore'
import { INSTRUCTIONS_VAZIAS } from '@domain/agent'
import type {
  Address,
  AgentInstructions,
  CashbackSite,
  PaymentDiscount,
  PreferredSite,
  SiteKnowledge,
} from '@domain/agent'
import type { AgentInstructionsRepository } from '@domain/ports/agent-instructions-repository'
import { textOr, toIso } from './converters'
import { HOUSEHOLD_ID } from './household-repository'

/** Um documento fixo — a casa tem um contexto, não vários. */
const DOC_ID = 'agentInstructions'

/**
 * O contexto de agente da casa, em `households/{hid}/settings/agentInstructions`.
 *
 * ## Ler com desconfiança, e nunca falhar por causa de um campo
 *
 * Todo campo aqui é digitado por gente e lido por um modelo. Um `reasons` que
 * virou string, um `preferredSites` que virou objeto — nada disso justifica
 * derrubar a tela de ajustes, que é justamente onde a pessoa conseguiria
 * consertar. Campo irreconhecível vira o vazio do seu tipo e o resto abre.
 *
 * É o oposto do `parseExtraction` das notas, que rejeita a nota inteira quando
 * uma linha não fecha: lá o dado vira dinheiro e um erro silencioso mente sobre
 * um valor; aqui o pior caso é um agente ler menos contexto do que existia.
 */
export function createFirebaseAgentInstructionsRepository(db: Firestore): AgentInstructionsRepository {
  const ref = doc(db, 'households', HOUSEHOLD_ID, 'settings', DOC_ID)

  return {
    async load() {
      const snap = await getDoc(ref)
      if (!snap.exists()) return INSTRUCTIONS_VAZIAS
      const d = snap.data()
      return {
        goal: textOr(d.goal, ''),
        preferredSites: lista(d.preferredSites, site),
        cashbackSites: lista(d.cashbackSites, cashback),
        paymentDiscounts: lista(d.paymentDiscounts, desconto),
        address: endereco(d.address),
        localNote: textOr(d.localNote, ''),
        siteKnowledge: lista(d.siteKnowledge, conhecimento),
        updatedAt: toIso(d.updatedAt) ?? '',
      }
    },

    async save(i) {
      // `updatedAt` do servidor, e não da tela: dois celulares com relógios
      // diferentes gravariam ordens diferentes para a mesma edição.
      await setDoc(ref, { ...semUpdatedAt(i), updatedAt: serverTimestamp() })
    },
  }
}

/**
 * O que a tela manda menos o carimbo — que é do servidor.
 *
 * Campo a campo de propósito: `setDoc` substitui o documento, então um campo a
 * mais que a tela trouxesse por engano seria gravado como contexto.
 */
function semUpdatedAt(i: AgentInstructions): Omit<AgentInstructions, 'updatedAt'> {
  return {
    goal: i.goal,
    preferredSites: i.preferredSites,
    cashbackSites: i.cashbackSites,
    paymentDiscounts: i.paymentDiscounts,
    address: i.address,
    localNote: i.localNote,
    siteKnowledge: i.siteKnowledge,
  }
}

/** Um item ilegível some; a lista inteira ilegível vira vazia. */
function lista<T>(cru: unknown, de: (item: Record<string, unknown>) => T | null): T[] {
  if (!Array.isArray(cru)) return []
  const saida: T[] = []
  for (const item of cru) {
    if (typeof item !== 'object' || item === null) continue
    const convertido = de(item as Record<string, unknown>)
    if (convertido !== null) saida.push(convertido)
  }
  return saida
}

/** Sem host, a linha não identifica site nenhum — e some. */
function site(o: Record<string, unknown>): PreferredSite | null {
  const host = textOr(o.host, '')
  if (host === '') return null
  return {
    host,
    name: textOr(o.name, ''),
    reasons: linhas(o.reasons),
    firstPurchaseUsed: o.firstPurchaseUsed === true,
  }
}

function cashback(o: Record<string, unknown>): CashbackSite | null {
  const name = textOr(o.name, '')
  const host = textOr(o.host, '')
  if (name === '' && host === '') return null
  return { name, host, rate: textOr(o.rate, '') }
}

/** Sem forma de pagamento, a observação não se aplica a nada. */
function desconto(o: Record<string, unknown>): PaymentDiscount | null {
  const method = textOr(o.method, '')
  if (method === '') return null
  return { method, note: textOr(o.note, '') }
}

function conhecimento(o: Record<string, unknown>): SiteKnowledge | null {
  const host = textOr(o.host, '')
  if (host === '') return null
  return {
    host,
    title: textOr(o.title, ''),
    baseUrl: textOr(o.baseUrl, ''),
    steps: linhas(o.steps),
    tips: linhas(o.tips),
  }
}

function endereco(cru: unknown): Address {
  if (typeof cru !== 'object' || cru === null) return INSTRUCTIONS_VAZIAS.address
  const o = cru as Record<string, unknown>
  return {
    street: textOr(o.street, ''),
    complement: textOr(o.complement, ''),
    district: textOr(o.district, ''),
    city: textOr(o.city, ''),
    state: textOr(o.state, ''),
    cep: textOr(o.cep, ''),
  }
}

function linhas(cru: unknown): string[] {
  if (!Array.isArray(cru)) return []
  return cru.filter((x): x is string => typeof x === 'string')
}
